import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, UsersIcon, BuildingIcon, FilterIcon, XIcon, MapIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, RESIDENCE_OPTIONS, type Coord } from '../../data/communications';
import { countActiveFilters, summarizePlaces } from '../../utils/audience';
import { useInternalTeams } from '../../hooks/useInternalTeams';
import { AudienceMap } from './AudienceMap';
import { AudiencePlaces } from './AudiencePlaces';
import { AudienceFilters } from './AudienceFilters';
import type { AudiencePlace, AudienceState } from '../../types/composer';
interface AudienceSelectorProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
  audienceTotal: number;
}

/**
 * Audience = Lieu (whole city, or places that add up) × Qui (filters that
 * narrow it down). The summary bar spells out the combination.
 */
export function AudienceSelector({
  audience,
  onChange,
  audienceTotal
}: AudienceSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [drawing, setDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState<Coord[]>([]);
  const [showMap, setShowMap] = useState(false);
  const {
    scope,
    places
  } = audience;
  const filtersCount = countActiveFilters(audience);
  const internalTeams = audience.internalTeams ?? [];
  const teamDirectory = useInternalTeams();
  const setPlaces = (next: AudiencePlace[]) => onChange({
    places: next
  });
  const toggleSector = (sectorId: string) => {
    const existing = places.find(p => p.kind === 'sector' && p.sectorId === sectorId);
    setPlaces(existing ? places.filter(p => p.id !== existing.id) : [...places, {
      id: `pl-${sectorId}-${Date.now()}`,
      kind: 'sector',
      sectorId
    }]);
  };
  const finishDrawing = () => {
    if (draftPoints.length >= 3) {
      setPlaces([...places, {
        id: `pl-area-${Date.now()}`,
        kind: 'area',
        points: draftPoints
      }]);
    }
    setDraftPoints([]);
    setDrawing(false);
  };
  const cancelDrawing = () => {
    setDraftPoints([]);
    setDrawing(false);
  };

  // Values of one criterion are OR-ed; different criteria are AND-ed.
  const orSep = lang === 'fr' ? ' ou ' : ' or ';

  // Filter chips shown in the summary, each removable in one click.
  const filterChips: {
    key: string;
    label: string;
    clear: () => void;
  }[] = [...(audience.selectedAges.length > 0 ? [{
    key: 'age',
    label: AGE_BRACKETS.filter(a => audience.selectedAges.includes(a.id)).map(a => a.label[lang]).join(orSep),
    clear: () => onChange({
      selectedAges: []
    })
  }] : []), ...(audience.selectedDepartments.length > 0 ? [{
    key: 'dep',
    label: DEPARTMENTS.filter(d => audience.selectedDepartments.includes(d.id)).map(d => d.label[lang]).join(orSep),
    clear: () => onChange({
      selectedDepartments: []
    })
  }] : []), ...(internalTeams.length > 0 ? [{
    key: 'team',
    label: teamDirectory.filter(team => internalTeams.includes(team.id)).map(team => team.name).join(orSep),
    clear: () => onChange({
      internalTeams: []
    })
  }] : []), ...(audience.residence !== 'all' ? [{
    key: 'res',
    label: RESIDENCE_OPTIONS.find(r => r.value === audience.residence)?.label[lang] ?? '',
    clear: () => onChange({
      residence: 'all'
    })
  }] : [])];
  const whereLabel = scope === 'all' ? t('Toute la ville', 'Entire city') : places.length === 0 ? t('Aucun lieu', 'No place') : summarizePlaces(places, lang);
  const scopeOptions: {
    id: AudienceState['scope'];
    icon: typeof UsersIcon;
    label: string;
    hint: string;
  }[] = [{
    id: 'all',
    icon: BuildingIcon,
    label: t('Toute la ville', 'Entire city'),
    hint: t('Tous les citoyens inscrits', 'Every registered citizen')
  }, {
    id: 'places',
    icon: MapPinIcon,
    label: t('Lieux précis', 'Specific places'),
    hint: t('Secteurs, adresses, rayons ou zones', 'Districts, addresses, radii or areas')
  }];
  return <section className="space-y-6" data-id="element-3839" data-mp="1nvwz29g989s6">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3840" data-mp="1nwothgy481gv">{t('Audience', 'Audience')}</h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 divide-y divide-slate-100" data-id="element-3841" data-mp="1j3vqoym7j37f">
        {/* 1 — WHERE */}
        <div className="p-6 space-y-4" data-id="element-3842" data-mp="1e4j50h72o26f">
          <div className="flex items-baseline gap-2" data-id="element-3843" data-mp="2bm114cdso1h2">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3844" data-mp="2bnkjq9mxaom0">
              1
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3845" data-mp="2e7qogjjd2j3m">{t('Où', 'Where')}</h3>
            {scope === 'places' && places.length > 1 && <span className="text-xs text-slate-400" data-id="element-3846" data-mp="2o4myitowrdaa">
                {t('Personne située dans au moins une de ces zones', 'Anyone located in at least one of these areas')}
              </span>}
          </div>

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="radiogroup" aria-label={t('Où', 'Where')} data-id="element-3847" data-mp="2x379t9s52koh">
            {scopeOptions.map(o => {
            const Icon = o.icon;
            const active = scope === o.id;
            return <button key={o.id} type="button" role="radio" aria-checked={active} title={o.hint} onClick={() => {
              if (o.id === 'all') cancelDrawing();
              onChange({
                scope: o.id
              });
            }} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3848" data-mp="3gtke2p1qivzc">
                  <Icon className="w-4 h-4" data-id="element-3849" data-mp="24fkj9khiciyk" />
                  {o.label}
                </button>;
          })}
          </div>

          <AnimatePresence initial={false} data-id="element-3850" data-mp="1zjdehv0uldi7">
            {scope === 'places' && <motion.div key="places" initial={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} animate={{
            height: 'auto',
            opacity: 1,
            // Let the "Ajouter un lieu" dropdown overflow once expanded.
            transitionEnd: {
              overflow: 'visible'
            }
          }} exit={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} transition={{
            duration: 0.2
          }} className="relative z-20" data-id="element-3851" data-mp="2dgiilph042at">
                <div className="pt-1 space-y-4" data-id="element-3852" data-mp="3rdbm6wefp3eo">
                  <AudiencePlaces places={places} onChange={setPlaces} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onStartDrawing={() => {
                setDraftPoints([]);
                setDrawing(true);
              }} onCancelDrawing={cancelDrawing} onFinishDrawing={finishDrawing} data-id="element-3853" data-mp="3rck1igmdkq36" />
                  {(places.length > 0 || drawing) && <div className="space-y-2" data-id="element-3854" data-mp="16nw20k9q0cy0">
                      {!drawing && <button type="button" onClick={() => setShowMap(s => !s)} aria-expanded={showMap} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-3855" data-mp="1bm7d0ybr4tht">
                          <MapIcon className="w-3.5 h-3.5" data-id="element-3856" data-mp="2ae7394adar7k" />
                          {showMap ? t('Masquer la carte', 'Hide map') : t('Voir sur la carte', 'Show on map')}
                          <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showMap ? 'rotate-180' : ''}`} data-id="element-3857" data-mp="21hgb32yxrwi9" />
                        </button>}
                      {(showMap || drawing) && <AudienceMap places={places} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onAddDraftPoint={c => setDraftPoints(prev => [...prev, c])} data-id="element-3858" data-mp="1nhfbdqueqv4d" />}
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* 2 — WHO */}
        <div className="p-6 space-y-4" data-id="element-3859" data-mp="1dejf5s1pfcjn">
          <div className="flex items-baseline gap-2" data-id="element-3860" data-mp="1he0l0ml1mbpc">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3861" data-mp="1hfvbegf32w0t">
              2
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3862" data-mp="pn4k8kc9bxif">{t('Qui', 'Who')}</h3>
            <span className="text-xs text-slate-400" data-id="element-3863" data-mp="tneptvw50lwr">{t('Facultatif', 'Optional')}</span>
          </div>
          <AudienceFilters audience={audience} onChange={onChange} data-id="element-3864" data-mp="yo2dq8p2kmmr" />
        </div>

        {/* Summary */}
        <div className="px-6 py-4 bg-slate-50/70 rounded-b-xl flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-id="element-3865" data-mp="252tl8v8p75pr">
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-600" data-id="element-3866" data-mp="2a4988sufrmas">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700" data-id="element-3867" data-mp="16cwn94nz1dxn">
              <MapPinIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-3868" data-mp="1g4kb6vlbkcud" />
              {whereLabel}
            </span>
            {filterChips.map(chip => <React.Fragment key={chip.key} data-id="element-3869" data-mp="1l5q3wtezc03w">
                <span className="text-xs font-semibold uppercase text-slate-400 px-0.5" data-id="element-3870" data-mp="ngy43dcvuq2r">
                  {t('et', 'and')}
                </span>
                <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700 max-w-[260px]" data-id="element-3871" data-mp="shc72gzmn9tx">
                  <FilterIcon className="w-3.5 h-3.5 text-[#4F46E5] flex-shrink-0" data-id="element-3872" data-mp="11gpfd6c0lsd2" />
                  <span className="truncate" data-id="element-3873" data-mp="28sg4dqbojmme">{chip.label}</span>
                  <button type="button" onClick={chip.clear} aria-label={`${t('Retirer le filtre', 'Remove filter')} ${chip.label}`} className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-3874" data-mp="2cv2yxyzqjlna">
                    <XIcon className="w-3 h-3" data-id="element-3875" data-mp="3oka7rs5vteyk" />
                  </button>
                </span>
              </React.Fragment>)}
            {filtersCount === 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-3876" data-mp="3emmez7ru6hj1">
                {t('· aucun filtre', '· no filter')}
              </span>}

          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3877" data-mp="39lzt9vmafy8r">
            <UsersIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3878" data-mp="35l64l010ufet" />
            <span className="text-sm text-slate-500" data-id="element-3879" data-mp="35nshzgalfp4y">
              {audienceTotal > 0 && filtersCount > 0 ? t('environ', 'about') + ' ' : ''}
              <span className="text-lg font-bold text-[#0F172A]" data-id="element-3880" data-mp="13tji9bs2r8oo">
                {audienceTotal.toLocaleString(locale)}
              </span>{' '}
              {t('personnes ciblées', 'people targeted')}
            </span>
          </div>
        </div>
      </div>
    </section>;
}